/**
 * A reader's own shelf in one world, as the API sends and accepts it.
 *
 *   GET /api/me/worlds/:world/shelf          every entry, and the quiz result
 *   PUT /api/me/worlds/:world/shelf/:work    one book's entry: set, or cleared
 *   PUT /api/me/worlds/:world/quiz           the latest quiz result
 *
 * Private: only the signed-in, verified reader it belongs to ever reads or
 * writes it, and there is no public profile. Deleting the account deletes
 * all of it (migrations 0011 and 0012 cascade from the user).
 *
 * An entry says at least one thing: a reading status, that the reader owns
 * the book, a rating, or a note. One that says nothing is not stored; setting
 * everything empty removes it.
 */

import { z } from 'zod'
import type { MessageKey } from './message-keys.ts'

export const SHELF_STATUSES = ['want', 'reading', 'read'] as const
export type ShelfStatus = (typeof SHELF_STATUSES)[number]

/** A private note's length, in characters, as migration 0011 allows. */
export const NOTE_MAX_LENGTH = 2000

const key = (k: MessageKey): string => k

export const shelfEntryInputSchema = z.object({
  status: z.enum(SHELF_STATUSES, { error: key('error.bad_request') }).nullable(),
  owned: z.boolean({ error: key('error.bad_request') }),
  rating: z.number({ error: key('error.bad_request') }).int().min(1).max(5).nullable(),
  // Trimmed, and an empty note is no note.
  note: z
    .string({ error: key('error.bad_request') })
    .trim()
    .max(NOTE_MAX_LENGTH, { error: key('error.note_too_long') })
    .nullable()
    .transform((note) => (note === null || note === '' ? null : note)),
})

export type ShelfEntryInput = z.infer<typeof shelfEntryInputSchema>

/** One book on the reader's shelf. `work` is the book's slug. */
export interface ShelfEntry {
  work: string
  status: ShelfStatus | null
  owned: boolean
  rating: number | null
  note: string | null
  updated_at: string
}

export interface SavedQuizResult {
  /** The quiz's own answer string, as in `?a=`. */
  answers: string
  first: string
  second: string
  taken_at: string
}

export interface ShelfResponse {
  entries: ShelfEntry[]
  quiz: SavedQuizResult | null
}

export const quizResultInputSchema = z.object({
  answers: z.string({ error: key('error.bad_request') }).max(32, { error: key('error.bad_request') }),
})

/** Whether an entry would say nothing, and so is removed rather than stored. */
export function isEmptyEntry(entry: ShelfEntryInput): boolean {
  return entry.status === null && !entry.owned && entry.rating === null && entry.note === null
}
